import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, Observable, retry, switchMap, take, timer } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { setSessionActive } from 'src/app/stores/global';
import { selectSettings } from 'src/app/stores/settings/settings.selector';
import { isSizeSelectionAvailable } from 'src/app/stores/spaces/spaces.selector';
import { MAX_RETRY_CODE_GENERATION } from 'src/environments/runtime';

import { Dispense } from '../../interfaces/IDispense';
import { CreateDispense } from '../../models/dispense.models';
import {
  ACTION_TYPES,
  DELIVERY_LIST_TYPE,
  DISPENSE_STATES,
  MOBILE_TYPE,
  ORDER_ID_TYPE,
  PRESCRIPTIVE_FLOW,
  RETAIL_PATHS,
  RETAIL_SCREEN,
  SCAN_TYPE,
  SIDES,
  TOAST_TYPES,
  VALID_RETAIL_CODE_TYPES,
  VIEW_TYPES,
} from '../../utils/constants';
import { isLoadCode, isPickupCode, isReclaimCode } from '../../utils/schedule-codes.utils';
import { Utils } from '../../utils/utils';
import { resolveValidateCodeErrorTitle } from '../../utils/validate-code-error.utils';
import { ApiErrorHandlerService } from '../api-error-handler/api-error-handler.service';
import { HelmsdeepService } from '../helmsdeep/helmsdeep.service';
import { HelmsdeepValidateCodeErrorResponse } from '../helmsdeep/helmsdeep-validate-code-error.response';
import { ValidateCodeResponse } from '../helmsdeep/types';
import { KillSwitchService } from '../kill-switch/kill-switch.service';

@Injectable({ providedIn: 'root' })
export class Retail {
  private side = SIDES.FRONT;
  private frodoContext = VIEW_TYPES.RETAIL;
  private noSpaceAvailable = 'common.noSpaceAvailable';
  private readonly SCAN_CODES_URL = `/${VIEW_TYPES.RETAIL}/${RETAIL_PATHS.ENTER_SCAN_CODE}`;
  private readonly SIZE_SELECTOR_URL = `/${VIEW_TYPES.RETAIL}/${RETAIL_PATHS.SIZE_SELECTOR}`;
  private readonly COMPARTMENT_INDICATOR_URL = `/${VIEW_TYPES.RETAIL}/${RETAIL_PATHS.COMPARTMENT_INDICATOR}`;

  constructor(
    private utils: Utils,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private helmsdeepService: HelmsdeepService,
    private store: Store,
    private killSwitchService: KillSwitchService,
    private translateService: TranslateService
  ) {}

  init(processOrder: IProcessOrder): void {
    this.utils.setLoadingValue(true);
    this.helmsdeepService.getAvailableSpacesCount().subscribe(
      (spaces) => {
        if (spaces.count > 0) {
          switch (processOrder.type) {
            case MOBILE_TYPE:
              this.processByMobile(processOrder);
              break;
            case ORDER_ID_TYPE:
              this.processByOrderId(processOrder);
              break;
            case DELIVERY_LIST_TYPE:
              this.handleDeliveryFlow(processOrder.scheduleId, processOrder.dispenseId);
              break;
          }
        } else {
          this.utils.setLoadingValue(false);
          this.apiErrorHandlerService.showMessage(this.noSpaceAvailable, TOAST_TYPES.Error);
        }
      },
      (error) => {
        this.utils.setLoadingValue(false);
        this.apiErrorHandlerService.getErrorMessage(error);
      }
    );
  }

  processByActionType(processOrder: IProcessOrder): void {
    this.utils.processOrderByActionType(processOrder).subscribe((response) => {
      const currentPositions = processOrder.positions || [];
      processOrder.positions = [...currentPositions, response.position];
      processOrder.spaceId = response.spaceId;
      processOrder.direction = response.direction;

      this.goToCompartmentIndicator(processOrder);
    });
  }

  goToCompartmentIndicator(processOrder: IProcessOrder): void {
    this.utils.redirect(this.COMPARTMENT_INDICATOR_URL, {
      action: processOrder.action,
      direction: processOrder.direction,
      positions: JSON.stringify(processOrder.positions),
      scheduleId: processOrder.scheduleId,
      spaceId: processOrder.spaceId,
      dispenseId: processOrder.dispenseId,
      presence:
        processOrder.action === ACTION_TYPES.PICKUP || processOrder.action === ACTION_TYPES.RECLAIM
          ? true
          : false,
      code: processOrder.code,
      addSpace: processOrder.addSpace,
      type: processOrder.type,
    });
  }

  validateCodeType(code: string): void {
    this.utils.setLoadingValue(true);
    this.helmsdeepService
      .validateCode(code, VALID_RETAIL_CODE_TYPES)
      .pipe(
        catchError((error) => {
          this.mapValidateCodeError(error, code);
          return EMPTY;
        })
      )
      .subscribe((scheduleNdispenses) => {
        const schedule = scheduleNdispenses;
        localStorage.setItem('schedule', JSON.stringify(schedule));

        const dispense = schedule.dispenses[0];
        this.processCodeValidation(schedule, dispense.id);
      });
  }

  redirectRestingView(isLaunchScreenEnable: boolean): void {
    if (isLaunchScreenEnable) {
      this.utils.redirect(RETAIL_SCREEN.URL);
    } else {
      this.utils.redirect(this.SCAN_CODES_URL);
    }
  }

  handleAddSpace(processOrder: IProcessOrder): void {
    this.store
      .select(isSizeSelectionAvailable)
      .pipe(take(1))
      .subscribe((sizeSelectionAvailable) => {
        if (sizeSelectionAvailable) {
          this.goToSizeSelector(processOrder);
          return;
        }

        this.createAndLoadDispense(processOrder);
      });
  }

  createAndLoadDispense(processOrder: IProcessOrder): void {
    this.createDispense(processOrder)
      .pipe(
        this.retryStrategy(),
        catchError((error) => this.handleCreateDispenseError(error))
      )
      .subscribe((updatedProcessOrder) => {
        this.processByActionType(updatedProcessOrder);
      });
  }

  processCodeValidation(schedule: ValidateCodeResponse, dispenseId: string): void {
    const scheduleId = schedule.scheduleId;
    const codeType = schedule.codeType;

    if (isLoadCode(codeType)) {
      this.store.dispatch(setSessionActive({ value: true }));
      this.handleDeliveryFlow(scheduleId, dispenseId);
      this.killSwitchService.handleKillSwitchMode();
      return;
    }

    if (isPickupCode(codeType)) {
      this.handlePickupFlow(scheduleId, dispenseId);
      return;
    }

    if (isReclaimCode(codeType)) {
      this.store.dispatch(setSessionActive({ value: true }));
      this.handleReclaimFlow(scheduleId, dispenseId);
      this.killSwitchService.handleKillSwitchMode();
      return;
    }
  }

  private handleDeliveryFlow(scheduleId: string, dispenseId: string): void {
    this.helmsdeepService.getAvailableSpacesCount().subscribe(
      (spaces) => {
        if (spaces.count > 0) {
          const processOrder: IProcessOrder = {
            action: ACTION_TYPES.DELIVERY,
            scheduleId,
            dispenseId,
            type: SCAN_TYPE,
            side: this.side,
            frodoContext: this.frodoContext,
          };
          this.initLoadWithOptionalSizeSelection(processOrder);
        } else {
          this.utils.setLoadingValue(false);
          this.apiErrorHandlerService.showMessage(this.noSpaceAvailable, TOAST_TYPES.Error);
          this.utils.redirect(this.SCAN_CODES_URL);
        }
      },
      (error) => {
        this.utils.setLoadingValue(false);
        this.apiErrorHandlerService.getErrorMessage(error);
      }
    );
  }

  private handlePickupFlow(scheduleId: string, dispenseId: string): void {
    const processOrder: IProcessOrder = {
      side: this.side,
      frodoContext: this.frodoContext,
      action: ACTION_TYPES.PICKUP,
      scheduleId,
      dispenseId,
    };

    this.processByActionType(processOrder);
  }

  private handleReclaimFlow(scheduleId: string, dispenseId: string): void {
    const processOrder: IProcessOrder = {
      side: this.side,
      frodoContext: this.frodoContext,
      action: ACTION_TYPES.RECLAIM,
      scheduleId,
      dispenseId,
    };

    this.processByActionType(processOrder);
  }

  private processByOrderId(processOrder: IProcessOrder): void {
    this.utils.createOrderByOrderIdType(processOrder).subscribe({
      next: (response) => {
        const order: IProcessOrder = {
          ...processOrder,
          code: response.loadCode,
          scheduleId: response.id,
          dispenseId: response.dispenses?.[0].id,
          action: ACTION_TYPES.DELIVERY,
          positions: response.positions,
          frodoContext: this.frodoContext,
          side: this.side,
        };

        this.initLoadWithOptionalSizeSelection(order);
      },
      error: (error) => {
        this.utils.setLoadingValue(false);
        this.apiErrorHandlerService.getErrorMessage(error);
      },
    });
  }

  private processByMobile(processOrder: IProcessOrder): void {
    this.utils.createOrderByMobileType(processOrder).subscribe({
      next: (response) => {
        processOrder.positions = response.positions;
        const order: IProcessOrder = {
          ...processOrder,
          code: response.loadCode,
          scheduleId: response.id,
          dispenseId: response.dispenses?.[0].id,
          action: ACTION_TYPES.DELIVERY,
          frodoContext: this.frodoContext,
          side: this.side,
        };

        this.initLoadWithOptionalSizeSelection(order);
      },
      error: (error) => {
        this.utils.setLoadingValue(false);
        this.apiErrorHandlerService.getErrorMessage(error);
      },
    });
  }

  private initLoadWithOptionalSizeSelection(processOrder: IProcessOrder): void {
    this.store
      .select(isSizeSelectionAvailable)
      .pipe(take(1))
      .subscribe((sizeSelectionAvailable) => {
        if (sizeSelectionAvailable) {
          this.goToSizeSelector(processOrder);
          return;
        }
        this.processByActionType(processOrder);
      });
  }

  private goToSizeSelector(processOrder: IProcessOrder): void {
    this.utils.setLoadingValue(false);
    this.utils.redirect(this.SIZE_SELECTOR_URL, {
      action: processOrder.action,
      scheduleId: processOrder.scheduleId,
      dispenseId: processOrder.dispenseId,
      type: processOrder.type,
      addSpace: processOrder.addSpace,
      positions: JSON.stringify(processOrder.positions || []),
    });
  }

  private createDispense(processOrder: IProcessOrder): Observable<IProcessOrder> {
    return this.helmsdeepService
      .getCurrentDispense(processOrder.scheduleId, DISPENSE_STATES.LOADING)
      .pipe(
        switchMap((verify: boolean) => this.buildDispense(processOrder.scheduleId, verify)),
        switchMap((newDispense: Dispense) =>
          this.helmsdeepService.createDispenses([newDispense]).pipe(
            map(() => ({
              ...processOrder,
              dispenseId: newDispense.id,
              code: newDispense.loadCode,
              positions: [...(processOrder.positions || [])],
            }))
          )
        )
      );
  }

  private buildDispense(scheduleId: string, verify: boolean): Observable<Dispense> {
    return this.store.select(selectSettings).pipe(
      take(1),
      map((settings) => {
        const createDispense = new CreateDispense(
          scheduleId,
          settings.accessCode.allowAlphanumeric,
          settings.accessCode.generatedCodeLength || settings.accessCode.length.maximum,
          PRESCRIPTIVE_FLOW,
          false,
          verify
        );

        return createDispense.newDispense;
      })
    );
  }

  private handleCreateDispenseError(error: unknown): Observable<never> {
    this.utils.setLoadingValue(false);
    this.apiErrorHandlerService.getErrorMessage(error);
    return EMPTY;
  }

  private retryStrategy(delayDurationMs = 1000) {
    const maxRetries = parseInt(MAX_RETRY_CODE_GENERATION, 10) - 1;
    return <T>(src: Observable<T>) =>
      src.pipe(
        retry({
          count: maxRetries,
          delay: () => timer(delayDurationMs),
        })
      );
  }

  private mapValidateCodeError(error: HelmsdeepValidateCodeErrorResponse, code: string): void {
    const title = resolveValidateCodeErrorTitle(error, code, this.translateService);
    if (title) {
      error.error.title = title;
    }

    this.utils.setLoadingValue(false);
    this.apiErrorHandlerService.getErrorMessage(error);
  }
}
